import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress, Link, useMediaQuery, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { login } from './utils/api';
import LoginIllustration from './logo.svg'; // Use logo as illustration for now

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorHeaders, setErrorHeaders] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorHeaders(null);
    if (!username || !password) {
      setError('Please enter both username and password.');
      return;
    }
    setLoading(true);
    try {
      const res = await login(username, password);
      const { token, user } = res.data;
      localStorage.setItem('jwt', token);
      localStorage.setItem('role', user.role);
      localStorage.setItem('username', user.username);
      if (user.role === 'business' && user.shopId) {
        localStorage.setItem('shopId', user.shopId);
      }
      if (user.role === 'admin') {
        navigate('/');
      } else if (user.role === 'business') {
        navigate('/business/dashboard');
      } else {
        setError('Unknown user role.');
      }
    } catch (err) {
      // Show a clear error message from the API response
      const apiMsg = err.response?.data?.detail || err.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(apiMsg);
      setErrorHeaders(null); // Hide headers from user
    } finally {
      setLoading(false);
    }
  };

  const quickFillDemo = (role) => {
    if (role === 'admin') {
      setUsername('admin@demo');
      setPassword('demo123');
    } else {
      setUsername('business@demo');
      setPassword('demo123');
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setForgotOpen(true);
    setTimeout(() => setForgotOpen(false), 2500);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 1, sm: 2 } }}>
      <Paper elevation={0} sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        maxWidth: { xs: '95vw', sm: 900 },
        width: '100%',
        borderRadius: { xs: 2, sm: 4 },
        overflow: 'hidden',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        mx: 'auto',
      }}>
        {/* Left Side: Illustration & Welcome */}
        <Box sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 4 },
          minWidth: { xs: '100%', sm: 320 },
          minHeight: { xs: 100, sm: 180 },
          maxHeight: { xs: 160, sm: 'none' },
        }}>
          <img src="/ShopZo-removebg-preview.png" alt="ShopZo Logo" style={{ height: 300, width: 300, maxWidth: '90%' }} />
          {/* <Typography variant="h4" fontWeight={800} mb={2} textAlign="center" sx={{ fontSize: { xs: 32, sm: 40 }, letterSpacing: 2, color: '#fff' }}>
            ShopZo
          </Typography> */}
          <Typography variant="body2" textAlign="center" mb={1} sx={{ fontSize: { xs: 12, sm: 16 } }}>
            Manage your business, invoices, and analytics.
          </Typography>
          <Typography variant="caption" textAlign="center" sx={{ opacity: 0.8, fontSize: { xs: 10, sm: 13 } }}>
            © {new Date().getFullYear()} Dexterrr Tech
          </Typography>
        </Box>
        {/* Right Side: Login Form */}
        <Box sx={{
          flex: 1,
          p: { xs: 1.5, sm: 5 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 0,
        }}>
          <Typography variant="h5" fontWeight={700} color="text.primary" mb={2} textAlign="center" sx={{ fontSize: { xs: 18, sm: 28 } }}>
            Login
          </Typography>
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Stack spacing={2}>
              <TextField
                label="Username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                fullWidth
                margin="normal"
                required
                autoFocus
                size={isMobile ? 'small' : 'medium'}
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                size={isMobile ? 'small' : 'medium'}
              />
              <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} mb={2}>
                <Link href="#" underline="hover" color="primary" fontWeight={600} onClick={handleForgotPassword} sx={{ fontSize: { xs: 12, sm: 15 } }}>
                  Forgot password?
                </Link>
              </Box>
              {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
              {errorHeaders && (
                <Box sx={{ mt: 1, bgcolor: '#f5f5f5', borderRadius: 1, p: 1, fontSize: 13 }}>
                  <Typography variant="caption" color="text.secondary">Response Headers:</Typography>
                  <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(errorHeaders, null, 2)}</pre>
                </Box>
              )}
              {forgotOpen && <Alert severity="info" sx={{ mt: 1 }}>Password reset link feature coming soon!</Alert>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 1, fontWeight: 700, minHeight: 40, fontSize: { xs: 15, sm: 18 }, letterSpacing: 1 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" justifyContent="center">
                <Typography variant="caption" color="text.secondary">Try demo credentials:</Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={() => quickFillDemo('admin')}>Admin Demo</Button>
                  <Button size="small" variant="outlined" onClick={() => quickFillDemo('business')}>Business Demo</Button>
                </Stack>
              </Stack>
            </Stack>
          </form>
        </Box>
      </Paper>
    </Box>
  );
} 
